import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Cookie, ChevronDown } from "lucide-react";
import { TEL_LINK, waLink, PHONE_DISPLAY } from "@/config/contact";

// C10. Footer — E2: links als rijen van 44px op mobiel.
const linkCls = "flex items-center min-h-11 md:min-h-0 md:inline text-gray-300 hover:text-[#20C7C9] transition-colors font-bold";
const linkClsSoft = "flex items-center min-h-11 md:min-h-0 md:inline text-gray-400 hover:text-[#20C7C9] transition-colors";
const colCls = "mt-2 md:mt-4 flex flex-col gap-0 md:gap-2.5 text-sm";

export default function Footer() {
  // Mobiel-ronde-2: Diensten, Werkgebied en Meer klappen in op mobiel; Contact blijft altijd open.
  const [openCols, setOpenCols] = useState({});
  const toggleCol = (name) => setOpenCols((s) => ({ ...s, [name]: !s[name] }));
  const colHead = (title, name) => (
    <p className="text-[13px] font-bold tracking-[0.2em] uppercase text-gray-400">
      <button
        type="button"
        onClick={() => toggleCol(name)}
        aria-expanded={!!openCols[name]}
        data-testid={`footer-col-${name}-toggle`}
        className="w-full md:w-auto flex md:inline-flex items-center justify-between md:justify-start gap-2 min-h-[44px] md:min-h-0 md:pointer-events-none text-left uppercase tracking-[0.2em]"
      >
        {title}
        <ChevronDown className={`md:hidden w-4 h-4 shrink-0 transition-transform duration-200 ${openCols[name] ? "rotate-180" : ""}`} />
      </button>
    </p>
  );
  const colBodyCls = (name) => `${colCls} ${openCols[name] ? "" : "max-md:hidden"}`;
  return (
    <footer data-testid="site-footer" data-cta-location="footer" className="border-t border-white/10 pt-8 pb-8 md:pt-16 md:pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="hidden md:block font-display text-[14vw] leading-none font-black uppercase tracking-tighter text-white/5 select-none" aria-hidden="true">
          Spoedlekkage
        </p>
        <div className="mt-6 md:mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-10">
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <p className="flex items-center gap-2 font-display font-black text-xl uppercase tracking-tighter" data-testid="footer-logo">
              <img src="/logo-icon-64.webp" alt="Spoedlekkage.nl" width="64" height="66" loading="lazy" decoding="async" className="h-7 w-auto" />
              <span>Spoed<span className="text-[#20C7C9]">lekkage</span>.nl</span>
            </p>
            <p className="mt-3 md:mt-4 text-sm text-gray-400 leading-relaxed">
              24/7 lekkageservice in Noord-Holland, Utrecht en Almere. Onderdeel van de KNAP® Groep, samen met{" "}
              <a href="https://www.knaponderhoud.nl/" target="_blank" rel="noopener noreferrer" data-testid="footer-knap-onderhoud-link" className="inline-block py-3 -my-2 md:py-0 md:my-0 text-white underline underline-offset-4 hover:text-[#20C7C9] transition-colors">KNAP Onderhoud</a> en{" "}
              <a href="https://www.knapreiniging.nl/" target="_blank" rel="noopener noreferrer" data-testid="footer-knap-reiniging-link" className="inline-block py-3 -my-2 md:py-0 md:my-0 text-white underline underline-offset-4 hover:text-[#20C7C9] transition-colors">KNAP Reiniging</a>.
            </p>
            <img src="/knap-logo-wit.svg" alt="KNAP® Groep" width={187} height={38} loading="lazy" decoding="async" className="mt-4 md:mt-6 h-5 w-auto opacity-70" data-testid="footer-knap-logo" />
          </div>
          <div>
            <p className="text-[13px] font-bold tracking-[0.2em] uppercase text-gray-400">Contact</p>
            <a href={TEL_LINK} data-testid="footer-call-link" className="js-phone mt-4 flex items-center gap-3 min-h-11 font-display text-2xl font-black hover:text-[#20C7C9] transition-colors">
              <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="mt-1 md:mt-3 flex items-center gap-3 min-h-11 md:min-h-0 text-gray-300 hover:text-[#20C7C9] transition-colors font-bold">
              <MessageCircle className="w-5 h-5" /> WhatsApp ons
            </a>
            <p className="mt-3 md:mt-4 text-sm text-gray-400 leading-relaxed">
              Nieuwendammerdijk 526 M 12<br />1023 BX Amsterdam
            </p>
            <p className="mt-2 text-sm text-gray-400" data-testid="footer-kvk">Spoedlekkage.nl is een handelsnaam van Knap Groep B.V. · KvK 42198096 · btw NL870140784B01</p>
          </div>
          <div>
            {colHead("Diensten", "diensten")}
            <div data-collapsible="true" className={colBodyCls("diensten")}>
              <Link to="/lekdetectie" data-testid="footer-diensten-lekdetectie" className={linkCls}>Lekdetectie</Link>
              <Link to="/daklekkage" data-testid="footer-diensten-daklekkage" className={linkCls}>Daklekkage</Link>
              <Link to="/leidinglekkage" data-testid="footer-diensten-leidinglekkage" className={linkCls}>Leidinglekkage</Link>
              <Link to="/cv-lekkage" data-testid="footer-diensten-cv-lekkage" className={linkCls}>Cv-lekkage</Link>
              <Link to="/lekkage-badkamer" data-testid="footer-diensten-lekkage-badkamer" className={linkCls}>Lekkage badkamer</Link>
              <Link to="/waterschade" data-testid="footer-diensten-waterschade" className={linkCls}>Waterschade herstel</Link>
              <Link to="/lekkage-verzekering" data-testid="footer-diensten-verzekering" className={linkCls}>Lekkage en verzekering</Link>
              <Link to="/lekkage-vve" data-testid="footer-diensten-vve" className={linkCls}>Lekkage bij VvE's</Link>
            </div>
          </div>
          <div>
            {colHead("Werkgebied", "werkgebied")}
            <div data-collapsible="true" className={colBodyCls("werkgebied")}>
              <Link to="/lekkage-amsterdam" data-testid="footer-gebied-amsterdam" className={linkCls}>Amsterdam</Link>
              <Link to="/lekkage-utrecht" data-testid="footer-gebied-utrecht" className={linkCls}>Utrecht</Link>
              <Link to="/lekkage-almere" data-testid="footer-gebied-almere" className={linkCls}>Almere</Link>
              <Link to="/lekkage-amstelveen" data-testid="footer-gebied-amstelveen" className={linkCls}>Amstelveen</Link>
              <Link to="/lekkage-zaandam" data-testid="footer-gebied-zaandam" className={linkCls}>Zaandam</Link>
              <Link to="/lekkage-amersfoort" data-testid="footer-gebied-amersfoort" className={linkCls}>Amersfoort</Link>
              <Link to="/lekkage-hilversum" data-testid="footer-gebied-hilversum" className={linkCls}>Hilversum</Link>
              <Link to="/lekkage-haarlem" data-testid="footer-gebied-haarlem" className={linkCls}>Haarlem</Link>
            </div>
          </div>
          <div>
            {colHead("Meer", "meer")}
            <div data-collapsible="true" className={colBodyCls("meer")}>
              <Link to="/kennisbank" data-testid="footer-knowledge-link" className={linkCls}>Kennisbank</Link>
              <Link to="/kennisbank/water-uit-het-plafond" data-testid="footer-kb-article-link" className={linkClsSoft}>Water uit het plafond</Link>
              <Link to="/kennisbank/lekkage-wat-te-doen" data-testid="footer-kb-wat-te-doen-link" className={linkClsSoft}>Lekkage, wat te doen?</Link>
              <Link to="/over-ons" data-testid="footer-over-ons-link" className={linkClsSoft}>Over ons</Link>
              <Link to="/privacyverklaring" data-testid="footer-privacy-link" className={linkClsSoft}>Privacyverklaring</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
                data-testid="footer-cookie-settings-button"
                className="flex items-center gap-1.5 min-h-11 md:min-h-0 text-gray-400 hover:text-[#20C7C9] transition-colors text-left"
              >
                <Cookie className="w-3.5 h-3.5" /> Cookie-instellingen
              </button>
            </div>
          </div>
        </div>
        <div className="mt-6 md:mt-12 pt-6 md:pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-[13px] text-gray-500">
          <p>© 2026 Spoedlekkage.nl — Onderdeel van de KNAP® Groep</p>
          <p className="flex items-center gap-4">
            <Link to="/privacyverklaring" data-testid="footer-legal-privacy" className="hover:text-[#20C7C9] transition-colors">Privacyverklaring</Link>
            <Link to="/algemene-voorwaarden" data-testid="footer-legal-av" className="hover:text-[#20C7C9] transition-colors">Algemene voorwaarden</Link>
          </p>
          <p>24/7 bereikbaar • Persoonlijk contact</p>
        </div>
      </div>
    </footer>
  );
}
